/**
 * /audience as one CSV (spec §6, R12): provenance lines first, then two sections: box 1's segments, one empty row, and
 * box 2's ad sets, every row after the filters and the sort (never just the first 15), then "รวม", then the two rule
 * notes. Under the page's no-spend card both sections say the card's sentence instead of rows ("<channel>
 * ยังไม่ได้เชื่อมต่อ" when the one ticked channel is known not to be connected, spend or not, rules-04 G3 and H3).
 * The Search / Shopping note with its count and the unknown-currency count are provenance lines, as D3's file carries
 * its data notes. The note line for boxes 3–5 is not in the file.
 *
 * A NULL is written as the page shows it: "—" (D3's rule, and D1's and D2's since rules-04 D4). A missing ad-set ID
 * stays an empty cell (rules-04 D review Q3); every other NULL is "—". Money is raw with its currency in the header,
 * and ratios are percent numbers (D1's num / pct / unit). Words come from audienceText and D2's formats, so the file
 * says what the page says. Pure, except `downloadAudienceCsv`.
 *
 * @module services/audience/audienceCsv
 */
import type { ChannelFreshnessState } from '@/hooks/useLookerChannelFreshness';
import { CHANNEL_LABEL, campaignStateText, emptySentence, familyLabel, type TFn } from '@/services/channels/channelFormat';
import type { SegmentPoint } from '@/services/channels/channelTypes';
import { channelFilterText, objectiveFilterText } from '@/services/creatives/creativeText';
import { downloadCsv, type CsvCell } from '@/services/csv';
import { syncLine, utcMinute, type DashboardCsvLabels } from '@/services/looker/lookerCsv';
import type { DateRange } from '@/services/looker/lookerDateRange';
import { num, pct, unit } from '@/services/overview/boxTableParts';
import { DASH } from '@/services/overview/overviewFormat';
import { activeFilterText, emptyTextOf, ruleNotesOf, searchShoppingNote } from './audienceText';
import type { AudienceFilters, AudienceRow, AudienceTotal, AudienceView } from './audienceTypes';

export interface AudienceCsvInput {
  allWorkspaces: boolean;
  /** The workspace's name; null while it loads. */
  workspaceName: string | null;
  /** The period: D1's windows.current. */
  range: DateRange;
  view: AudienceView;
  filters: AudienceFilters;
  freshness: ChannelFreshnessState;
  /** audienceDataDateOf(freshness, view). */
  dataDate: string | null;
}

/** R12: a NULL is written as the page shows it. */
const orDash = (c: CsvCell): CsvCell => (c === null ? DASH : c);

const scopeOf = (input: AudienceCsvInput, l: DashboardCsvLabels): string =>
  (input.allWorkspaces ? l.allWorkspaces : input.workspaceName ?? '');

/** Section 1, box 1 as D2 writes its segment table: Segment · Share · CTR · CPC · n<500, then D2's line when one is n<500. */
const segmentRows = (segments: readonly SegmentPoint[], currency: string | null, t: TFn): CsvCell[][] => [
  [
    t('audience.col.segment'), unit(t('channels.breakdown.segmentShare'), '%'), unit(t('channels.breakdown.segmentCtr'), '%'),
    unit(t('channels.breakdown.segmentCpc'), currency), t('overview.lowSample'),
  ],
  ...segments.map((s): CsvCell[] => [
    familyLabel(s.family, t), orDash(pct(s.share)), orDash(pct(s.ctr)), orDash(num(s.cpc)), s.lowSample ? t('overview.lowSample') : null,
  ]),
  ...(segments.some((s) => s.lowSample) ? [[t('channels.breakdown.segmentLowSample')]] : []),
];

const tableColumns = (t: TFn, currency: string | null): string[] => [
  t('audience.col.adSet'), t('audience.csv.externalId'), t('audience.col.channel'), t('audience.col.segment'),
  t('audience.csv.campaign'), t('audience.csv.status'), unit(t('audience.col.spend'), currency), unit(t('audience.col.share'), '%'),
  t('audience.csv.impressions'), t('audience.csv.clicks'), unit(t('audience.col.ctr'), '%'), unit(t('audience.col.cpc'), currency),
  unit(t('audience.col.cpm'), currency), t('overview.lowSample'),
];

/** The eight figures every line carries, "รวม" included; the n<500 cell is the word or empty, as the page shows it. */
const figureCells = (m: AudienceTotal, t: TFn): CsvCell[] => [
  orDash(num(m.spend)), orDash(pct(m.share)), orDash(num(m.impressions)), orDash(num(m.clicks)),
  orDash(pct(m.ctr)), orDash(num(m.cpc)), orDash(num(m.cpm)), m.lowSample ? t('overview.lowSample') : null,
];

/** R4: the Status column writes every state in D2's words ("Active", "หยุด", "จบ", "จบ <date>", "—"). */
const rowCells = (r: AudienceRow, t: TFn): CsvCell[] => [
  // A missing ad-set ID is an absent dimension: an empty cell (rules-04 D review Q3).
  r.name, r.externalAdSetId, CHANNEL_LABEL[r.platform], familyLabel(r.family, t), r.campaignName,
  campaignStateText(r.state, t), ...figureCells(r, t),
];

/** "รวม": its five text cells empty, then the figures over every row. */
const totalCells = (total: AudienceTotal, t: TFn): CsvCell[] =>
  [t('channels.breakdown.total'), null, null, null, null, null, ...figureCells(total, t)];

/** Both sections: their titles, then the tables, the page's empty line, or the state a table that is not ready is in. */
export const audienceCsvRows = (input: AudienceCsvInput, l: DashboardCsvLabels, t: TFn): CsvCell[][] => {
  const segmentsTitle = [t('audience.box.segments')];
  const tableTitle = [t('audience.box.table')];
  const table = input.view.table;
  if (table.status !== 'ready') {
    const state = [table.status === 'failed' ? l.appFailed : l.loading];
    return [segmentsTitle, state, [], tableTitle, state];
  }
  const notes = ruleNotesOf(t).map((n) => [n]);
  const { data } = table;
  if (data.rows.length === 0) {
    // Under the card (rules-04 A3) the file says the card's sentence, as the page does; else the table's own empty line (R9).
    const { empty: card } = input.view;
    const empty = [card ? emptySentence(card, t) : emptyTextOf(data, scopeOf(input, l), input.filters, t)];
    return [segmentsTitle, empty, [], tableTitle, empty, ...notes];
  }
  const currency = input.view.money?.currency ?? null;
  return [
    segmentsTitle, ...segmentRows(data.segments, currency, t), [],
    tableTitle, tableColumns(t, currency), ...data.rows.map((r) => rowCells(r, t)), totalCells(data.total, t), ...notes,
  ];
};

/** R12's order; ISO dates, and the file's own time in UTC (a file has no viewer clock). */
export const audienceProvenance = (input: AudienceCsvInput, l: DashboardCsvLabels, t: TFn, now: Date): string[] => {
  const { range, view, filters: f } = input;
  return [
    t('audience.title'),
    scopeOf(input, l),
    t('audience.csv.periodLine', { start: range.start, end: range.end }),
    input.dataDate ? l.dataAsOf(input.dataDate) : null,
    t('audience.csv.filtersLine', {
      channels: channelFilterText(f.channels, t), objective: objectiveFilterText(f.objective, t),
      active: activeFilterText(f.activeOnly, view.hidden, t),
    }),
    view.money?.currency ? l.currency(view.money.currency) : null,
    view.unknownCurrency > 0 ? t('audience.notes.unknownCurrency', { n: view.unknownCurrency }) : null,
    searchShoppingNote(view.leftOut, t),
    syncLine(input.freshness, l),
    t('audience.csv.madeAt', { at: utcMinute(now.toISOString()) }),
  ].filter((line): line is string => Boolean(line));
};

/**
 * R11: a file needs the table drawn and the sync line read. While the facts load, or after they failed, there is
 * nothing to write (spec §6); a file written while the freshness read runs would miss its line.
 */
export const audienceCsvReady = (view: Pick<AudienceView, 'table'>, freshness: ChannelFreshnessState): boolean =>
  view.table.status === 'ready' && freshness.status !== 'loading';

export const audienceCsvFilename = (range: DateRange): string => `audience-targeting_${range.start}_${range.end}.csv`;

export const downloadAudienceCsv = (input: AudienceCsvInput, l: DashboardCsvLabels, t: TFn, now: Date = new Date()): void => {
  downloadCsv(audienceCsvFilename(input.range), audienceCsvRows(input, l, t), { provenance: audienceProvenance(input, l, t, now) });
};
